import { Suspense } from "react";

import { css } from "@compiled/react";
import { PreloadedQuery, usePreloadedQuery } from "react-relay";
import { graphql, VariablesOf } from "relay-runtime";

import { media } from "@atlaskit/primitives/responsive";

import { confluenceTemplateCollectionDetailsRelayPageQuery } from "@/__generated__/confluenceTemplateCollectionDetailsRelayPageQuery.graphql";
import ContentNotFoundErrorBoundary from "@/content/utils/content-not-found-error-boundary";
import { expectContent } from "@/content/utils/expectContent";

import { SharedHeaderFooterContainer } from "../../sections/shared-header-footer-container";
import { parseQueryLocale } from "../hooks/use-query-locale";
import { CollectionAboutSection } from "../sections/contentful-relay-sections/collection-about-section-contentful-relay";
import { CollectionGuideSection } from "../sections/contentful-relay-sections/collection-guide-section-contentful-relay";
import { CollectionHeaderSection } from "../sections/contentful-relay-sections/collection-header-section-contentful-relay";
import { CollectionRelatedSection } from "../sections/contentful-relay-sections/collection-related-section-contentful-relay";

export type ConfluenceTemplateCollectionDetailsRelayPageTemplateProps = {
    queryRef: PreloadedQuery<confluenceTemplateCollectionDetailsRelayPageQuery>;
};

const contentWrapper = css({
    "maxWidth": 1045,
    "margin": "0 auto",

    "@media (min-width: 30rem) and (max-width: 67.49rem)": {
        paddingInlineStart: 64,
        paddingInlineEnd: 64
    },
    [media.below.xs]: {
        paddingInlineStart: 32,
        paddingInlineEnd: 32
    }
});

const parseCollectionUrlSlug = (pathname: string) => {
    const [urlSlug, maybeCollectionsSlug] = pathname.split("/").reverse();
    return urlSlug && maybeCollectionsSlug === "collections" ? urlSlug : "";
};

const RELATED_COLLECTION_URL_SLUGS: Record<string, string[]> = {
    ["project-management"]: ["product-managers", "strategic-planning"],
    ["strategic-planning"]: ["project-management", "product-managers"]
};

const DEFAULT_RELATED_COLLECTION_URL_SLUGS = [
    "project-management",
    "strategic-planning"
];

export const ConfluenceTemplateCollectionDetailsRelayPageQuery = graphql`
    query confluenceTemplateCollectionDetailsContentfulRelayPageQuery(
        $locale: String
        $urlSlug: String!
        $relatedCollectionUrlSlugs: [String!]!
    ) {
        collections: templateCollectionCollection(
            limit: 1
            locale: $locale
            where: { urlSlug: $urlSlug }
        ) {
            items {
                howToUseThisCollection {
                    ...collectionGuideSectionContentfulRelayFragment
                }
                ...collectionHeaderSectionContentfulRelayFragment
                ...collectionAboutSectionContentfulRelayFragment
            }
        }
        relatedCollections: templateCollectionCollection(
            limit: 2
            locale: $locale
            where: { urlSlug_in: $relatedCollectionUrlSlugs }
        ) {
            items {
                ...collectionRelatedSectionContentfulRelayFragment
            }
        }
    }
`;

export const getConfluenceTemplateCollectionDetailsRelayPageQueryVars = (
    location: Location,
    partialVars?: Partial<
        VariablesOf<confluenceTemplateCollectionDetailsRelayPageQuery>
    >
): VariablesOf<confluenceTemplateCollectionDetailsRelayPageQuery> => {
    const locale = partialVars?.locale ?? parseQueryLocale(location);

    const urlSlug =
        partialVars?.urlSlug ?? parseCollectionUrlSlug(location.pathname);

    const relatedCollectionUrlSlugs =
        partialVars?.relatedCollectionUrlSlugs ??
        RELATED_COLLECTION_URL_SLUGS[urlSlug] ??
        DEFAULT_RELATED_COLLECTION_URL_SLUGS;

    return { locale, urlSlug, relatedCollectionUrlSlugs };
};

type CollectionDetailsContentRelayProps = {
    queryRef: PreloadedQuery<confluenceTemplateCollectionDetailsRelayPageQuery>;
};
const CollectionDetailsContentRelay = ({
    queryRef
}: CollectionDetailsContentRelayProps) => {
    const { collections, relatedCollections } =
        usePreloadedQuery<confluenceTemplateCollectionDetailsRelayPageQuery>(
            ConfluenceTemplateCollectionDetailsRelayPageQuery,
            queryRef
        );

    const collection = expectContent(collections.items[0]);

    return (
        <div data-testid="confluence-template-collection-details-content">
            <CollectionHeaderSection collection={collection} />
            <div css={contentWrapper}>
                <CollectionAboutSection collection={collection} />
                {collection.howToUseThisCollection && (
                    <CollectionGuideSection
                        collectionGuide={collection.howToUseThisCollection}
                    />
                )}
                {relatedCollections.items &&
                    relatedCollections.items.length > 0 && (
                        <CollectionRelatedSection
                            collections={relatedCollections.items}
                        />
                    )}
            </div>
        </div>
    );
};

export const ConfluenceTemplateCollectionDetailsRelayPageTemplate = ({
    queryRef
}: ConfluenceTemplateCollectionDetailsRelayPageTemplateProps) => {
    return (
        <SharedHeaderFooterContainer>
            <ContentNotFoundErrorBoundary>
                <Suspense>
                    <CollectionDetailsContentRelay queryRef={queryRef} />
                </Suspense>
            </ContentNotFoundErrorBoundary>
        </SharedHeaderFooterContainer>
    );
};
